import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  OccurrenceDispatch,
  OccurrenceReview,
  OccurrenceStep,
} from "../../../models/occurrence";
import { Occurrence } from "../../../models/occurrence";
import {
  getOccurrenceStepKeyOfValue,
  formatOccurrenceStepLabel,
  OccurrenceStepEnum,
} from "../../../enums/occurrenceStep";
import { formatMilisecondsInDaysHoursMinutes } from "../../../utils/dateUtils";
import StepFormDialog from "../dialogs/stepFormDialog";
import { fetchOccurrence } from "../../../api/fetchOccurence";
import { requiredFieldsByStep, getRequiredFields } from "./form/utils/required-fields-by-step";
import ErrorDialog from "../../../components/dialogs/ErrorDialog";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { formatFieldLabel } from "./form/utils/fields-labels";
import { Dialog, SimpleTooltip } from "@rio-cloud/rio-uikit";
import { formatOccurrenceTypeLabel } from "../../../enums/occurrenceType";
import { Step } from "../../../models/step";
import StepReleaseForm from "./form/stepReleaseForm";
import { fetchSchedulingByChassisList } from "../../../api/fetchScheduling";
import { Scheduling } from "../../../models/schedule";
import TasNumberRequiredDialog from "../dialogs/tasNumberRequiredDialog";
import { mustSelectTasForStage } from "../utils/tasStageGuard";
import StepHistoryModal from "./stepHistoryModal";

interface IGenericCardProps {
  occurrence: Occurrence;
  reloadOccurrenceList: (occurrenceIdToRemove?: string, occurrenceIdToAdd?: string) => Promise<void>
  partners?: { id: string; name: string }[];
  summaryStep?: OccurrenceStep | null;
  step: OccurrenceStepEnum;
  isValidStep: (requiredFields: string[], occurrence: Occurrence) => boolean;
  loading: boolean;
  save: (occurrence: Occurrence) => Promise<Occurrence | false>;
  changeStep: (
    service: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum
  ) => Promise<Occurrence | null | undefined>;
  getNotFilledRequiredFields: (
    requiredFields: string[],
    occurrence: Occurrence
  ) => string[];
  finishOccurrence: (occurrence: Occurrence, finalizationReasonType?: string, finalizationReasonDescription?: string, fromStepId?: number) => Promise<boolean>;
  disabledAllFields?: boolean;
  persona: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  finalized?: boolean;
  review: OccurrenceReview[];
  reviewStep: (
    occurrenceId: string,
    step: OccurrenceStepEnum,
    rate: number,
    comment?: string
  ) => Promise<void>;
  getReview: (occurrenceId: string) => Promise<void>;
  occurrenceStepAvailable: string[];
  upsertDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch
  ) => Promise<{
    wasCreated: boolean;
    error?: string;
    dispatch?: OccurrenceDispatch;
  }>;
  cancelDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch,
    reason: string,
    description: string
  ) => Promise<{
    wasCanceled: boolean;
    error?: string;
    dispatch?: OccurrenceDispatch;
  }>;
  removeDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch
  ) => Promise<void>;
  steps?: Step[];
  onRefresh: () => Promise<void>;
  isNextStamp?: boolean;
  renderCard?: (card: GenericCardView) => React.ReactNode;
}

export interface GenericCardView {
  status: "done" | "current" | "todo";
  startDate?: Date;
  endDate?: Date | null;
  estimatedTime?: string;
  estimatedRepairTime?: string;
  elapsedMs: number;
  typeLabel?: string;
  late: boolean;
  passageCount: number;
  statusText: string;
  statusLoading: boolean;
  statusDisabled: boolean;
  canChangeStatus: boolean;
  openForm: () => void;
  openHistory: () => void;
  requestStatusChange: (event: React.MouseEvent) => void;
  setStatusHovered: (hovered: boolean) => void;
}

const statusStyles = (isStepElapsedHours: boolean) => ({
  isCompleted: {
    border: "border-color-success",
    bg: "bg-green-50 dark:bg-green-900/40",
    text: "text-green-700 dark:text-green-300",
    ring: "border-green-200 dark:border-green-800",
    cardBg: isStepElapsedHours ? 'bg-orange-50 dark:bg-orange-900/30' : 'bg-white dark:bg-neutral-800',
    hoverBg: "hover:bg-[#2BA1AC]",
    hoverText: "hover:text-white hover:font-bold",
  },
  isInProgress: {
    border: "border-color-highlight",
    bg: "bg-blue-50 dark:bg-blue-900/40",
    text: "text-blue-700 dark:text-blue-300",
    ring: "border-blue-200 dark:border-blue-800",
    cardBg: isStepElapsedHours ? 'bg-orange-50 dark:bg-orange-900/30' : 'bg-white dark:bg-neutral-800',
    hoverBg: "hover:bg-[#2BA1AC]",
    hoverText: "hover:text-white hover:font-bold",
  },
  isOpen: {
    border: "border-color-dark",
    bg: "bg-gray-200 dark:bg-neutral-700",
    text: "text-gray-600 dark:text-neutral-300",
    ring: "border-gray-300 dark:border-neutral-600",
    cardBg: isStepElapsedHours ? 'bg-orange-50 dark:bg-orange-900/30' : 'bg-transparent',
    hoverBg: "hover:bg-[#2BA1AC]",
    hoverText: "hover:text-white hover:font-bold",
  },
});

export const GenericCard = (props: IGenericCardProps) => {
  const intl = useIntl();

  const [isHovered, setIsHovered] = useState(false);
  const [view, setView] = useState<"form" | "changeStep" | "comments" | "worklog" | "budgets" | "support" | null>(
    null
  );
  const [scheduling, setScheduling] = useState<Scheduling | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [onConfirmAction, setOnConfirmAction] = useState<(() => void) | null>(
    null
  );
  const [releaseFormDialog, setReleaseFormDialog] = useState(false)
  const [cancelReleaseDialog, setCancelReleaseDialog] = useState(false)
  const skipReleaseCloseConfirmRef = useRef(false)
  const [errorDialog, setErrorDialog] = useState({
    show: false,
    title: "",
    body: <></>,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingTasChange, setPendingTasChange] = useState<{
    occurrence: Occurrence;
    fromStepId: number;
    toStep: OccurrenceStepEnum;
  } | null>(null);
  const [tasModalLoading, setTasModalLoading] = useState(false);

  const requestCloseReleaseForm = () => {
    if (skipReleaseCloseConfirmRef.current) {
      skipReleaseCloseConfirmRef.current = false;
      return;
    }
    setCancelReleaseDialog(true);
  };

  const closeReleaseForm = () => {
    skipReleaseCloseConfirmRef.current = true;
    setCancelReleaseDialog(false);
    setReleaseFormDialog(false);
  };

  const handleReleaseFormDialogChange: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    const nextValue = typeof value === "function" ? value(releaseFormDialog) : value;
    if (!nextValue) {
      closeReleaseForm();
      return;
    }
    setReleaseFormDialog(true);
  };

  const checkOutAt = props.summaryStep?.dtEnd;

  const logs = props.summaryStep?.logs ?? [];
  const currentLog = logs[logs.length - 1];
  const isCurrentLogOngoing = !props.summaryStep?.dtEnd;
  const passageCount = logs.length;

  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [completionTime, setCompletionTime] = useState(0);
  useEffect(() => {
    if (!currentLog?.startDate) {
      setCompletionTime(0);
      return;
    }

    const startTime = new Date(currentLog.startDate).getTime();
    const endTime = isCurrentLogOngoing
      ? new Date().getTime()
      : new Date(currentLog.endDate).getTime();

    setCompletionTime(endTime - startTime);
  }, [props.summaryStep]);

  const isStepElapsedHours = () => {
    if (props.summaryStep?.dtEnd || !props.steps) return false;
    const currentElapsedHours = parseInt(completionTime.toString())
    const stepElapsedHours = props.steps?.find((s) => s.id === props.step)?.elapsedHours ?? 0;
    return currentElapsedHours > (stepElapsedHours * 60 * 60 * 1000);
  }

  const isCompleted = checkOutAt !== undefined && checkOutAt !== null;
  const isInProgress =
    (props.summaryStep && checkOutAt === undefined) || checkOutAt === null;

  let styles = statusStyles(isStepElapsedHours()).isOpen;
  if (isCompleted) {
    styles = statusStyles(isStepElapsedHours()).isCompleted;
  } else if (isInProgress) {
    styles = statusStyles(isStepElapsedHours()).isInProgress;
  }

  const getStatusText = () => {
    if (isLoading) return intl.formatMessage({ id: "intl-msg:occurrence.card.statusLoading" });

    if (isHovered) {
      if (!props.summaryStep) return intl.formatMessage({ id: "intl-msg:occurrence.card.startAction" });
      if (isInProgress) return intl.formatMessage({ id: "intl-msg:occurrence.card.finishAction" });
      if (isCompleted) return intl.formatMessage({ id: "intl-msg:occurrence.card.reopenAction" });
    }

    if (props.step === OccurrenceStepEnum.RELEASE && isCompleted && !props.occurrence.finished) {
      return intl.formatMessage({ id: "intl-msg:occurrence.card.notFinished" });
    }

    return !props.summaryStep
      ? intl.formatMessage({ id: "intl-msg:occurrence.card.notStarted" })
      : isInProgress
      ? intl.formatMessage({ id: "intl-msg:occurrence.step.inProgress" })
      : intl.formatMessage({ id: "intl-msg:occurrence.card.completedStatus" });
  };

  useEffect(() => {
    const fetchScheduling = async () => {
      const occurrence = props.occurrence;

      if (occurrence?.scheduleNumber && props.step === OccurrenceStepEnum.RELEASE) {
        const scheduling = (await fetchSchedulingByChassisList([occurrence.vehicleChassis])).find(
          (e) => e.scheduleNumber === occurrence.scheduleNumber,
        );
        setScheduling(scheduling);
      }
    }

    fetchScheduling()
  }, [])

  const handleStepChange = async () => {
    setIsLoading(true);

    try {
      const lastStepIndex = props.occurrenceStepAvailable.length - 1;
      const fromStep = props.occurrence.steps?.find((s) => s.dtStart && !s.dtEnd);
      const fromStepEnum = Object.keys(OccurrenceStepEnum).find(
        (key) => OccurrenceStepEnum[key] === fromStep?.step
      ) as OccurrenceStepEnum;

        const fromOccurrence = await fetchOccurrence(
        props.occurrence.id,
        props.persona,
        props.finalized
      );
      if (!fromOccurrence) {
        setIsLoading(false);
        return;
      }

      const isPreventive = fromOccurrence.occurrenceType === "P";
      const isRoadAssistance = fromOccurrence.occurrenceType === 'A' || fromOccurrence.occurrenceType === 'W';
      const isDealership = fromOccurrence.occurrenceType === 'D';

      const currentStepEnum = fromStepEnum || props.step;
      const stepKeyFromEnum = getOccurrenceStepKeyOfValue(currentStepEnum) || currentStepEnum;
      const stepKey = `${stepKeyFromEnum}${isPreventive ? "_PREVENTIVE_CHECKUP" : ""}`;
      
      let requiredFields = [
        ...getRequiredFields(stepKey as keyof typeof requiredFieldsByStep, fromOccurrence.occurrenceType),
      ];

      const isCallStep = currentStepEnum === OccurrenceStepEnum.CALL ||
        stepKeyFromEnum === 'CALL' || String(stepKeyFromEnum).startsWith('CALL');
      if (isCallStep && !isPreventive) {
        if (isDealership) {
          const dealershipAllowed = [
            'vehicleOdometer', 'vehicleLicensePlate', 'vehicleModel',
            'dealershipDn', 'emissionStandard', 'country', 'source',
          ];
          requiredFields = requiredFields.filter(f => dealershipAllowed.includes(f));
        } else {
          requiredFields = requiredFields.filter((f) => {
            if (isRoadAssistance && f === 'driverName') return false;
            if (f === 'vehicleStopped') return false;
            if (f === 'occurrenceType') return false;
            return true;
          });
        }
      }

      // Add parts validation if to step is PARTS or PARTS_IN_TRANSIT
      if(props.step === OccurrenceStepEnum.PARTS) {
        requiredFields.push('partOrders', 'partOrderParts');
      }
      if(props.step === OccurrenceStepEnum.PARTS_IN_TRANSIT) {
        requiredFields.push('partOrders', 'partOrderParts');
      }

      if (
        isInProgress &&
        props.step !== OccurrenceStepEnum.RELEASE &&
        OccurrenceStepEnum[
          props.occurrenceStepAvailable[
            lastStepIndex
          ] as keyof typeof OccurrenceStepEnum
        ] !== props.step
      ) {
        setView("changeStep");
        setIsLoading(false);
        return;
      }

      const currentStep = getOccurrenceStepKeyOfValue(props.step);
      if (
        props.occurrenceStepAvailable[lastStepIndex] === currentStep &&
        props.step !== OccurrenceStepEnum.RELEASE &&
        fromStepEnum === currentStep
      ) {
        const nextStepIndex =
          Object.values(OccurrenceStepEnum).indexOf(props.step) + 1;
        const nextStep = Object.values(OccurrenceStepEnum)[nextStepIndex];

        if (fromStep?.id && mustSelectTasForStage(nextStep, fromOccurrence)) {
          setPendingTasChange({ occurrence: fromOccurrence, fromStepId: fromStep.id, toStep: nextStep });
          setIsLoading(false);
          return;
        }

        fromStep?.id && await props.changeStep(props.occurrence, fromStep.id, nextStep);
        setIsLoading(false);
        return;
      }

      if (!fromStepEnum && props.step === OccurrenceStepEnum.CALL) {
        props.changeStep(props.occurrence, -1, props.step);
        setIsLoading(false);
        return;
      }

      const isConcludingRelease =
        isInProgress && props.step === OccurrenceStepEnum.RELEASE;

      if (!fromStepEnum) {
        setErrorDialog({
          show: true,
          title: isConcludingRelease
            ? intl.formatMessage({ id: "intl-msg:occurrence.card.errorCompleteStepTitle" })
            : intl.formatMessage({ id: "intl-msg:occurrence.card.errorInitStepTitle" }),
          body: (
            <div>
              <div className="mb-2">
                {isConcludingRelease
                  ? intl.formatMessage({ id: "intl-msg:occurrence.card.errorCompleteReleaseBody" })
                  : intl.formatMessage({ id: "intl-msg:occurrence.card.errorStartReleaseBody" })}
              </div>
              <div className="mb-2">{intl.formatMessage({ id: "intl-msg:occurrence.step.requiredFields" })}</div>
              <ul className="list-disc list-inside">
                {props
                  .getNotFilledRequiredFields(requiredFields, props.occurrence)
                  .map((field) => (
                    <li key={field}>
                      {formatFieldLabel(intl, field)}
                    </li>
                  ))}
              </ul>
            </div>
          ),
        });
        setIsLoading(false);
        return;
      }

    
      if (!props.isValidStep(requiredFields, fromOccurrence)) {
        setErrorDialog({
          show: true,
          title: isConcludingRelease
            ? intl.formatMessage({ id: "intl-msg:occurrence.card.errorCompleteStepTitle" })
            : intl.formatMessage({ id: "intl-msg:occurrence.card.errorInitStepTitle" }),
          body: (
            <div>
              <div className="mb-2">
                {isConcludingRelease
                  ? intl.formatMessage({ id: "intl-msg:occurrence.card.errorRequiredFieldsBeforeCompleteBody" })
                  : intl.formatMessage({ id: "intl-msg:occurrence.card.errorRequiredFieldsBeforeAdvanceBody" })}
              </div>
              <div className="mb-2">{intl.formatMessage({ id: "intl-msg:occurrence.step.requiredFields" })}</div>
              <ul className="list-disc list-inside">
                {props
                  .getNotFilledRequiredFields(requiredFields, fromOccurrence)
                  .map((field) => (
                    <li key={field}>
                      {formatFieldLabel(intl, field)}
                    </li>
                  ))}
              </ul>
            </div>
          ),
        });
        setIsLoading(false);
        return;
      }

      if (isInProgress && props.step === OccurrenceStepEnum.RELEASE) {
        if (props.isNextStamp) {
          setReleaseFormDialog(true);
          return;
        }

        const closed = await closeOcurrence();
        if (closed) {
          await props.reloadOccurrenceList(props.occurrence?.id);
        }
        return;

      } else if (fromStep?.id && mustSelectTasForStage(props.step, fromOccurrence)) {
        setPendingTasChange({ occurrence: fromOccurrence, fromStepId: fromStep.id, toStep: props.step });
        setIsLoading(false);
        return;
      }

      if (fromStep?.step === props.step) {
        return;
      }

      fromStep?.id && await props.changeStep(props.occurrence, fromStep.id, props.step);
    } catch (error: any) {
      console.error("Erro na API:", error);
      
      const errorCode = error?.code || error?.response?.data?.code;
      const errorMessage = error?.message || error?.response?.data?.message;
      
      if (errorCode === "47" || errorCode === 47) {
        setErrorDialog({
          show: true,
          title: intl.formatMessage({ id: "intl-msg:occurrence.card.errorStepChangedByOtherTitle" }),
          body: (
            <div>
            {intl.formatMessage({ id: "intl-msg:occurrence.card.errorStepChangedByOtherBody" })}
            </div>
          ),
        });
      } else if (errorMessage) {
        setErrorDialog({
          show: true,
          title: intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepTitle" }),
          body: <div>{errorMessage}</div>,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const closeOcurrence = async (): Promise<boolean> => {
    if (!props.occurrence) return false;
    const fromStep = props.occurrence.steps?.find((s) => s.dtStart && !s.dtEnd);

    let success = true;
    try {
      await props.finishOccurrence(props.occurrence, undefined, undefined, fromStep?.id);
    } catch (error) {
      success = false;
    }

    if (!success) {
      setErrorDialog({
        show: true,
        title: intl.formatMessage({ id: "intl-msg:occurrence.step.errorOccurrenceTitle" }),
        body: (
          <div>
            {intl.formatMessage({ id: "intl-msg:occurrence.step.errorSupportBody" })}
          </div>
        ),
      });
      return false;
    }

    return true;
  }

  const canChangeStatus = props.persona !== "MANAGER" && !props.finalized;

  const openForm = () => {
    props.summaryStep && setView("form");
    props.getReview(props.occurrence.id);
  };

  const requestStatusChange = (e: React.MouseEvent) => {
    if (isLoading || !canChangeStatus) return;

    e.stopPropagation();

    setOnConfirmAction(() => () => handleStepChange());
    setShowConfirmDialog(true);
  };

  const setStatusHovered = (hovered: boolean) => {
    canChangeStatus && setIsHovered(hovered);
  };

  return (
    <>
      {props.renderCard ? props.renderCard({
        status: isCompleted ? "done" : isInProgress ? "current" : "todo",
        startDate: currentLog?.startDate ?? props.summaryStep?.dtStart,
        endDate: props.summaryStep?.dtEnd,
        estimatedTime: props.summaryStep?.estimatedTime,
        estimatedRepairTime: props.step === OccurrenceStepEnum.REPAIR ? props.occurrence.estimateTimeRepair : undefined,
        elapsedMs: completionTime,
        typeLabel: formatOccurrenceTypeLabel(intl, props.occurrence.occurrenceType),
        late: isStepElapsedHours(),
        passageCount,
        statusText: getStatusText(),
        statusLoading: isLoading,
        statusDisabled: !!props.occurrence.finished,
        canChangeStatus,
        openForm,
        openHistory: () => setShowHistoryModal(true),
        requestStatusChange,
        setStatusHovered,
      }) : (
      <div
        className="p-[10px] bg-[#C7D1D9] dark:bg-neutral-900"
        style={{ height: "307px", boxSizing: "border-box", width: "340px" }}
      >
        <div
          key={props.occurrence.id}
          className={`w-[320px] border  ${
            props.summaryStep && !props.occurrence.finished ? "cursor-pointer" : ""
          } ${styles.border} rounded-lg p-4 flex flex-col justify-between  ${
            styles.cardBg
          } text-size-14 text-gray-800 dark:text-neutral-100 ${isStepElapsedHours() ? "!bg-orange-50 dark:!bg-orange-900/30" : ""}`}
          style={{ height: "285px", borderWidth: "1.7px" }}
          onClick={openForm}
        >
          <div>
            <div className="flex justify-between items-center">
              <span>{intl.formatMessage({ id: "intl-msg:occurrence.step.start" })}</span>
              <span>
                {(currentLog?.startDate ?? props.summaryStep?.dtStart)?.toLocaleString(intl.locale, {
                  hour: "2-digit",
                  minute: "2-digit",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }) ?? "-"}
              </span>
            </div>
            {passageCount > 1 && (
              <div className="flex justify-end mb-1">
                <SimpleTooltip content={intl.formatMessage({ id: "intl-msg:kanban.genericCards.viewPassageHistory" })} placement="top">
                  <button
                    type="button"
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 text-size-12 font-medium"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHistoryModal(true);
                    }}
                  >
                    <span className="rioglyph rioglyph-refresh text-size-12" />
                    <span>{intl.formatMessage({ id: "intl-msg:kanban.genericCards.passage" }, { count: passageCount })}</span>
                    <span className="rioglyph rioglyph-info-sign text-size-12" />
                  </button>
                </SimpleTooltip>
              </div>
            )}
            <div className="flex justify-between">
              <span>{intl.formatMessage({ id: "intl-msg:occurrence.step.completion" })}</span>
              <span>
                {props.summaryStep?.dtEnd?.toLocaleString(intl.locale, {
                  hour: "2-digit",
                  minute: "2-digit",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }) ?? "-"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span>{intl.formatMessage({ id: "intl-msg:occurrence.card.estimatedTime" })}</span>
              <div className="flex items-center gap-1 text-gray-500 dark:text-neutral-400">
                <span className="rioglyph rioglyph-time-alt" />
                <span>{props.summaryStep?.estimatedTime ?? "-"}</span>
              </div>
            </div>
            {props.step === OccurrenceStepEnum.REPAIR && props.occurrence.estimateTimeRepair && (
              <div className="flex justify-between items-center">
                <span>{intl.formatMessage({ id: "intl-msg:kanban.genericCards.estimatedRepairTime" })}</span>
                <div className="flex items-center gap-1 text-gray-500 dark:text-neutral-400">
                  <span className="rioglyph rioglyph-time-alt" />
                  <span>{props.occurrence.estimateTimeRepair}</span>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span>{intl.formatMessage({ id: "intl-msg:occurrence.card.elapsedTime" })}</span>
              <div className="flex items-center gap-1 font-bold">
                <span className="rioglyph rioglyph-time-alt" />
                <span>
                  {completionTime
                    ? formatMilisecondsInDaysHoursMinutes(
                        parseInt(completionTime.toString())
                      )
                    : "-"}
                </span>
              </div>
            </div>
            {formatOccurrenceTypeLabel(intl, props.occurrence.occurrenceType) && (
              <>
                <div className="flex justify-between mb-2">
                  <span className="text-md text-gray-600 dark:text-neutral-400">{intl.formatMessage({ id: "intl-msg:occurrence.card.type" })}</span>
                  <span className="text-md font-medium">
                    {formatOccurrenceTypeLabel(intl, props.occurrence.occurrenceType)}
                  </span>
                </div>
              </>
            )}
          </div>

          {isStepElapsedHours() && (
            <div className="flex justify-end">
              <SimpleTooltip content={intl.formatMessage({ id: "intl-msg:occurrence.card.timeExceeded" })} placement="top">
                <span className="rioglyph rioglyph-warning-sign text-yellow-600 text-size-h5" />
              </SimpleTooltip>
            </div>
          )}

          <button
            className={`
              !mt-8 w-full text-center py-1.5 rounded-full font-medium 
              ${!isLoading && styles.bg} ${!isLoading && styles.text} ${!isLoading && styles.ring} 
              ${
                props.persona === "MANAGER" || props.finalized
                  ? ""
                  : styles.hoverBg
              } 
              ${
                props.persona === "MANAGER" || props.finalized
                  ? ""
                  : styles.hoverText
              } 
              transition-colors duration-200 
              ${isStepElapsedHours() ? "!bg-orange-100 !text-orange-700 hover:!bg-orange-200 hover:!text-orange-700" : ""}
              ${isLoading ? "btn btn-loading" : ""}
            `}
            onMouseEnter={() => setStatusHovered(true)}
            onMouseLeave={() => setStatusHovered(false)}
            disabled={props.occurrence.finished}
            onClick={requestStatusChange}
          >
            {getStatusText()}
          </button>
        </div>
      </div>
      )}

      {props.step && (
        <StepFormDialog
          reloadOccurrenceList={props.reloadOccurrenceList}
          showDialog={!!view}
          onClose={() => setView(null)}
          occurrence={props.occurrence}
          partners={props.partners}
          summaryStep={props.summaryStep}
          step={props.step}
          isValidStep={props.isValidStep}
          loading={props.loading}
          save={props.save}
          disabledAllFields={
            !!props.summaryStep?.dtEnd || props.disabledAllFields
          }
          changeStep={props.changeStep}
          view={view as "form" | "changeStep" | "comments" | "worklog" | "budgets" | "support"}
          setView={setView}
          getNotFilledRequiredFields={props.getNotFilledRequiredFields}
          finishOccurrence={props.finishOccurrence}
          persona={props.persona}
          finalized={props.finalized}
          review={props.review}
          reviewStep={props.reviewStep}
          occurrenceStepAvailable={props.occurrenceStepAvailable}
          upsertDispatch={props.upsertDispatch}
          cancelDispatch={props.cancelDispatch}
          removeDispatch={props.removeDispatch}
        />
      )}

      <ErrorDialog
        show={errorDialog.show}
        title={errorDialog.title}
        body={errorDialog.body}
        onClose={async () => {
          setErrorDialog({ show: false, title: "", body: <></> });
          if (errorDialog.title === intl.formatMessage({ id: "intl-msg:occurrence.card.errorStepChangedByOtherTitle" })) {
            setIsRefreshing(true);
            try {
              await props.onRefresh();
            } finally {
              setIsRefreshing(false);
            }
          }
        }}
      />

      <Dialog
          show={releaseFormDialog}
          title={intl.formatMessage({ id: "intl-msg:occurrence.step.release" })}
          body={
            releaseFormDialog ? (
              <StepReleaseForm
                scheduling={scheduling} 
                occurrence={props.occurrence} 
                setReleaseFormDialog={handleReleaseFormDialogChange}
                closeFirstModal={() => props.reloadOccurrenceList(props?.occurrence?.id)}
                closeOcurrence={closeOcurrence}
              />
            ) : null
          }
          className='exampleDialog'
          showCloseButton
          bsSize='md'
          onClose={requestCloseReleaseForm}
      />

      <ConfirmDialog
        show={cancelReleaseDialog}
        title={intl.formatMessage({ id: "intl-msg:occurrence.cancelReleaseTitle" })}
        body={intl.formatMessage({ id: "intl-msg:occurrence.cancelReleaseBody" })}
        onClose={() => setCancelReleaseDialog(false)}
        onConfirm={closeReleaseForm}
      />

      <Dialog
        show={showConfirmDialog}
        title={intl.formatMessage({ id: "intl-msg:occurrence.card.confirmation" })}
        body={
          <div>
            {isCompleted && (
              <>
                {intl.formatMessage(
                  { id: "intl-msg:occurrence.card.confirmReopenStep" },
                  { strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>, stepName: formatOccurrenceStepLabel(intl, props.step) }
                )}
              </>
            )}
            {isInProgress && !isCompleted && (
              <>
                {intl.formatMessage(
                  { id: "intl-msg:occurrence.card.confirmAdvanceStep" },
                  { strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>, stepName: formatOccurrenceStepLabel(intl, props.step) }
                )}
              </>
            )}
            {!props.summaryStep && (
              <>
                {intl.formatMessage(
                  { id: "intl-msg:occurrence.card.confirmStartStep" },
                  { strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>, stepName: formatOccurrenceStepLabel(intl, props.step) }
                )}
              </>
            )}
          </div>
        }
        footer={
          <div className="display-flex justify-content-end gap-2">
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              {intl.formatMessage({ id: "intl-msg:occurrence.card.cancel" })}
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                setShowConfirmDialog(false);
                onConfirmAction?.();
              }}
            >
              {intl.formatMessage({ id: "intl-msg:occurrence.card.confirm" })}
            </button>
          </div>
        }
        bsSize={Dialog.SIZE_XS}
        onClose={() => setShowConfirmDialog(false)}
      />

      <TasNumberRequiredDialog
        show={!!pendingTasChange}
        occurrence={pendingTasChange?.occurrence ?? null}
        loading={tasModalLoading}
        onCancel={() => setPendingTasChange(null)}
        onConfirm={async (tasNumber) => {
          if (!pendingTasChange) return;

          setTasModalLoading(true);
          try {
            const saved = await props.save({ ...pendingTasChange.occurrence, tasNumber });
            if (!saved) {
              setTasModalLoading(false);
              setErrorDialog({
                show: true,
                title: intl.formatMessage({ id: "intl-msg:kanban.genericCards.errorSaveTasTitle" }),
                body: (
                  <div>
                    {intl.formatMessage({ id: "intl-msg:occurrence.step.errorSupportBody" })}
                  </div>
                ),
              });
              return;
            }

            await props.changeStep(
              { ...pendingTasChange.occurrence, tasNumber },
              pendingTasChange.fromStepId,
              pendingTasChange.toStep
            );
            setTasModalLoading(false);
            setPendingTasChange(null);
          } catch (error: any) {
            setTasModalLoading(false);
            setErrorDialog({
              show: true,
              title: intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepTitle" }),
              body: <div>{error?.message ?? intl.formatMessage({ id: "intl-msg:occurrence.step.errorSupportBody" })}</div>,
            });
          }
        }}
        />
      <StepHistoryModal
        show={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        stepLabel={formatOccurrenceStepLabel(intl, props.step)}
        logs={logs}
        isLastLogOngoing={isCurrentLogOngoing}
      />
    </>
  );
};

export const SkeletonGenericCard = (props: { index: number }) => {
  const styles = {
    border: "border-color-darkest",
    bg: "bg-gray-200 dark:bg-neutral-700",
    text: "text-gray-600 dark:text-neutral-300",
    ring: "border-gray-300 dark:border-neutral-600",
    cardBg: "bg-transparent",
    hoverBg: "hover:bg-[#2BA1AC]",
    hoverText: "hover:text-white hover:font-bold",
  };
  const skeletonBlock = "h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2";

  return (
    <div className="p-[10px] bg-[#C7D1D9] dark:bg-neutral-900" style={{ height: "307px", boxSizing: "border-box", width: "340px" }}>
      <div
        key={props.index}
        className={`w-[320px] border ${styles.border} rounded-lg p-4 ${styles.cardBg} text-size-14 text-gray-800 dark:text-neutral-100`}
        style={{ height: "285px" }}
      >
        <div>
          <div className="flex justify-between">
            <div className={skeletonBlock}></div>
            <div className={skeletonBlock}></div>
          </div>
          <div className="flex justify-between">
            <div className={skeletonBlock}></div>
            <div className={skeletonBlock}></div>
          </div>
          <div className="flex justify-between items-center">
            <div className={skeletonBlock}></div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-neutral-400">
              <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className={skeletonBlock}></div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className={skeletonBlock}></div>
            <div className="flex items-center gap-1 font-bold">
              <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className={skeletonBlock}></div>
            </div>
          </div>
        </div>

        <div className="!mt-8 w-full h-[28px] animate-pulse rounded-full bg-gray-200 dark:bg-neutral-700 mb-2"></div>
      </div>
    </div>
  );
};
